import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { UserPlus, Copy, Check, Gift, Users, CalendarPlus, WhatsappLogo } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReferralPanel() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const fetchInfo = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/referral/info`, { withCredentials: true });
      setInfo(data);
    } catch (error) { console.error("Referral fetch error:", error.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchInfo(); }, [fetchInfo]);

  const getReferralUrl = () => {
    if (!info) return '';
    return `${window.location.origin}?ref=${info.referral_code}`;
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(getReferralUrl());
      setCopied(true);
      toast.success('Lien de parrainage copié !');
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error('Impossible de copier'); }
  };

  const handleWhatsApp = () => {
    const text = `Salut ! Rejoins Shanga Pro pour créer des posts marketing BTP qui attirent des clients. Utilise mon lien : ${getReferralUrl()}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  if (loading) return (
    <div className="flex items-center justify-center py-8">
      <div className="animate-spin w-6 h-6 border-2 border-[#3F3F46] border-t-[#8B5CF6] rounded-full" />
    </div>
  );

  return (
    <div className="space-y-4 animate-fade-in" data-testid="referral-panel">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-4 text-center">
          <Users size={22} weight="duotone" className="mx-auto text-[#A78BFA] mb-1" />
          <p className="text-2xl font-black text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{info?.referrals_count || 0}</p>
          <p className="text-xs text-[#71717A]">Filleul{(info?.referrals_count || 0) > 1 ? 's' : ''}</p>
        </div>
        <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-4 text-center">
          <CalendarPlus size={22} weight="duotone" className="mx-auto text-[#A78BFA] mb-1" />
          <p className="text-2xl font-black text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{info?.referral_months_earned || 0}</p>
          <p className="text-xs text-[#71717A]">Mois gagnés</p>
        </div>
        <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-4 text-center">
          <Gift size={22} weight="duotone" className="mx-auto text-[#A78BFA] mb-1" />
          <p className="text-2xl font-black text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>1</p>
          <p className="text-xs text-[#71717A]">Mois / filleul</p>
        </div>
      </div>

      {/* Referral Link */}
      <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-5">
        <div className="flex items-center gap-2 mb-3">
          <UserPlus size={20} weight="duotone" className="text-[#A78BFA]" />
          <h3 className="font-bold text-base text-[#F4F4F5]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Votre lien de parrainage</h3>
        </div>
        <p className="text-sm text-[#A1A1AA] mb-4">Partagez ce lien. Pour chaque ami qui s'inscrit, vous gagnez <strong className="text-[#A78BFA]">1 mois Pro gratuit</strong>.</p>

        <div className="flex gap-2 mb-4">
          <div className="flex-1 h-12 px-4 bg-[#09090B] border border-[#27272A] rounded-lg flex items-center overflow-hidden">
            <span className="text-sm text-[#A1A1AA] truncate" data-testid="referral-url">{getReferralUrl()}</span>
          </div>
          <button onClick={handleCopy} data-testid="copy-referral-btn"
            className={`h-12 px-4 rounded-lg font-medium text-sm flex items-center gap-1.5 transition-all shrink-0 ${
              copied ? 'bg-green-500/15 text-green-400 border border-green-500/30' : 'bg-[#8B5CF6] text-white hover:bg-[#7C3AED]'
            }`}>
            {copied ? <><Check size={16} weight="bold" /> Copié</> : <><Copy size={16} weight="duotone" /> Copier</>}
          </button>
        </div>

        <button onClick={handleWhatsApp} data-testid="share-whatsapp-btn"
          className="w-full h-12 bg-[#25D366]/15 text-[#25D366] border border-[#25D366]/30 rounded-lg font-medium text-sm hover:bg-[#25D366]/25 flex items-center justify-center gap-2 transition-colors">
          <WhatsappLogo size={20} weight="duotone" /> Partager sur WhatsApp
        </button>

        {info?.referral_code && (
          <p className="text-xs text-[#71717A] mt-3 text-center">
            Code : <span className="font-mono text-[#A78BFA]">{info.referral_code}</span>
          </p>
        )}
      </div>

      {/* Referral list */}
      {info?.referrals && info.referrals.length > 0 && (
        <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-5">
          <h3 className="font-bold text-sm text-[#F4F4F5] mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Vos filleuls</h3>
          <div className="space-y-2">
            {info.referrals.map((r) => (
              <div key={r.id} className="flex items-center justify-between px-3 py-2 bg-[#09090B] rounded-lg border border-[#27272A]">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-[#8B5CF6]/15 flex items-center justify-center">
                    <UserPlus size={14} className="text-[#A78BFA]" />
                  </div>
                  <span className="text-sm text-[#E4E4E7]">{r.referred_name}</span>
                </div>
                <span className="text-xs text-[#71717A]">
                  {new Date(r.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
